import { type PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";

import { REQUEST_STATUSES } from "@/constants";

interface ExperienceMediaItem {
  id: number;
  mediaItemId: number;
  propertyId: number;
  experienceId: number;
  createdAt?: string;
  updatedAt?: string;
  createdById?: number | null;
}

const defaultState = {
  experienceMediaItems: {} as Record<number, ExperienceMediaItem>,
  status: REQUEST_STATUSES.IDLE as REQUEST_STATUSES,
  err: null as string | null,
} as const;

interface ExperienceMediaItemState {
  experienceMediaItems: Record<number, ExperienceMediaItem>;
  status: REQUEST_STATUSES;
  err: string | null;
}

interface ExperienceMediaItemsState {
  [key: string]: ExperienceMediaItemState;
}

interface SetExperienceMediaItemsRequestStatusPayload {
  propertyId: string | number;
  experienceId: string | number;
  status: REQUEST_STATUSES;
}

interface SetExperienceMediaItemsPayload {
  propertyId: string | number;
  experienceId: string | number;
  experienceMediaItems: Record<number, ExperienceMediaItem>;
  status?: REQUEST_STATUSES;
  err?: string | null;
}

interface AddExperienceMediaItemsPayload {
  propertyId: string | number;
  experienceId: string | number;
  experienceMediaItems: Record<number, ExperienceMediaItem>;
}

interface RemoveExperienceMediaItemsPayload {
  propertyId: string | number;
  experienceId: string | number;
  experienceMediaItems: Record<number, ExperienceMediaItem>;
}

type ExperienceId = string | number | null | undefined;

const initialState: ExperienceMediaItemsState = {};

const createExperienceMediaItemState = (): ExperienceMediaItemState => ({ ...defaultState });

const getStateKey = (propertyId: string | number, experienceId: string | number): string =>
  `${propertyId}/${experienceId}`;

const experienceMediaItemsSlice = createSlice({
  name: "experienceMediaItems",
  initialState,
  reducers: {
    setExperienceMediaItemsRequestStatus: (
      state,
      action: PayloadAction<SetExperienceMediaItemsRequestStatusPayload>,
    ) => {
      const { propertyId, experienceId, status } = action.payload;
      const key = getStateKey(propertyId, experienceId);

      if (!state[key]) {
        state[key] = createExperienceMediaItemState();
      }
      state[key].status = status as REQUEST_STATUSES;
    },

    setExperienceMediaItems: (state, action: PayloadAction<SetExperienceMediaItemsPayload>) => {
      const { propertyId, experienceId, experienceMediaItems, status, err } = action.payload;
      const key = getStateKey(propertyId, experienceId);

      state[key] = {
        experienceMediaItems: experienceMediaItems || {},
        status: (status || REQUEST_STATUSES.SUCCEEDED) as REQUEST_STATUSES,
        err: err || null,
      };
    },

    addExperienceMediaItems: (state, action: PayloadAction<AddExperienceMediaItemsPayload>) => {
      const { propertyId, experienceId, experienceMediaItems } = action.payload;
      const key = getStateKey(propertyId, experienceId);

      if (!state[key]) {
        state[key] = createExperienceMediaItemState();
      }

      state[key].experienceMediaItems = experienceMediaItems;
    },

    removeExperienceMediaItems: (state, action: PayloadAction<RemoveExperienceMediaItemsPayload>) => {
      const { propertyId, experienceId, experienceMediaItems } = action.payload;
      const key = getStateKey(propertyId, experienceId);

      if (!state[key]) {
        state[key] = createExperienceMediaItemState();
      }

      state[key].experienceMediaItems = experienceMediaItems;
    },
  },
});

export const {
  setExperienceMediaItemsRequestStatus,
  setExperienceMediaItems,
  addExperienceMediaItems,
  removeExperienceMediaItems,
} = experienceMediaItemsSlice.actions;

export default experienceMediaItemsSlice.reducer;

export const experienceMediaItemsSelector = (
  propertyId: string | number | null | undefined,
  experienceId: ExperienceId,
) =>
  createSelector(
    (state: { experienceMediaItems: ExperienceMediaItemsState }) => {
      if (!experienceId || !propertyId) return undefined;
      return state.experienceMediaItems[getStateKey(propertyId, experienceId)];
    },
    (experienceMediaItemsData) => ({
      experienceMediaItems: experienceMediaItemsData?.experienceMediaItems || {},
      status: experienceMediaItemsData?.status || REQUEST_STATUSES.IDLE,
      err: experienceMediaItemsData?.err || null,
    }),
  );
